import React from "react";
import { toast } from "sonner";
import {
  FileText,
  Captions,
  Type,
  ListVideo,
  Crop,
  Download,
  Music4,
  Volume2,
  Languages,
} from "lucide-react";
import { SCENES, OFFSETS, BRAND } from "@/data/adPackage";
import {
  buildScriptSheet,
  buildSRT,
  buildShotList,
  buildCutGuide,
  triggerDownload,
} from "@/lib/downloads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScriptDownloads() {
  const dl = (label, filename, content, mime) => {
    triggerDownload(filename, content, mime);
    toast.success("Download started", { description: label });
  };

  const files = [
    {
      testid: "dl-script",
      icon: FileText,
      title: "Script Sheet",
      desc: "VO + on-screen text + scene timings",
      onClick: () => dl("Script sheet (.txt)", "BiteGoFood_Script_Sheet.txt", buildScriptSheet()),
    },
    {
      testid: "dl-srt-vo",
      icon: Captions,
      title: "Subtitles — VO version",
      desc: "Burned-in caption source (.srt)",
      onClick: () => dl("VO subtitles (.srt)", "BiteGoFood_Subtitles_VO.srt", buildSRT("vo")),
    },
    {
      testid: "dl-srt-novo",
      icon: Type,
      title: "Subtitles — No-VO version",
      desc: "Text-led caption source (.srt)",
      onClick: () => dl("No-VO subtitles (.srt)", "BiteGoFood_Subtitles_NoVO.srt", buildSRT("novo")),
    },
    {
      testid: "dl-srt-te",
      icon: Languages,
      title: "Telugu Subtitles (.srt)",
      desc: "తెలుగు captions matching Telugu VO",
      onClick: () => dl("Telugu subtitles (.srt)", "BiteGoFood_Subtitles_Telugu.srt", buildSRT("vo", "te")),
    },
    {
      testid: "dl-shotlist",
      icon: ListVideo,
      title: "Shot List",
      desc: "Per-scene shots, in/out & durations",
      onClick: () => dl("Shot list (.txt)", "BiteGoFood_Shot_List.txt", buildShotList()),
    },
    {
      testid: "dl-cutguide",
      icon: Crop,
      title: "9:16 Cut Guide",
      desc: "Vertical-safe crop instructions",
      onClick: () => dl("9:16 cut guide (.txt)", "BiteGoFood_9x16_Cut_Guide.txt", buildCutGuide()),
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Script sheet */}
      <div className="lg:col-span-7">
        <div className="rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="font-display font-black text-white text-xl">Full VO Script</h3>
              <p className="text-xs text-neutral-500">16:9 master · {OFFSETS.total}s · Neutral Indian-English</p>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400 glass rounded-full px-3 py-1">Approved</span>
          </div>
          <div className="divide-y divide-white/5 max-h-[520px] overflow-y-auto no-scrollbar">
            {SCENES.map((s) => {
              const start = OFFSETS.map[s.id];
              return (
                <div key={s.id} data-testid={`script-row-${s.index}`} className="px-5 py-4 flex gap-4">
                  <div className="shrink-0 text-right w-16">
                    <div className="font-display font-black text-orange-400">{s.index}</div>
                    <div className="text-[10px] text-neutral-500 tabular-nums">{start}s–{start + s.dur}s</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-neutral-500 font-semibold mb-0.5">{s.chapter}</div>
                    <p className="text-sm text-white/90 leading-relaxed">“{s.vo}”</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Downloads */}
      <div className="lg:col-span-5 space-y-4">
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-4 h-4 text-orange-400" />
            <h3 className="font-display font-black text-white text-xl">Downloads</h3>
          </div>
          <div className="space-y-2.5">
            {files.map((f) => {
              const Ico = f.icon;
              return (
                <button
                  key={f.testid}
                  data-testid={f.testid}
                  onClick={f.onClick}
                  className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-neutral-900 hover:border-orange-500/50 px-3.5 py-3 text-left transition-colors duration-200"
                >
                  <span className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center shrink-0">
                    <Ico className="w-4 h-4 text-white" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-white">{f.title}</span>
                    <span className="block text-[11px] text-neutral-500">{f.desc}</span>
                  </span>
                  <Download className="w-4 h-4 text-neutral-600" />
                </button>
              );
            })}
          </div>
        </div>

        {/* VO audio clips */}
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Music4 className="w-4 h-4 text-orange-400" />
            <h3 className="font-display font-black text-white text-xl">Voice-Over Audio</h3>
          </div>
          <p className="text-[11px] text-neutral-500 mb-4">Generated narration · one clip per scene</p>

          <div className="text-[10px] uppercase tracking-[0.18em] text-orange-400 font-semibold mb-2">Indian-English</div>
          <div className="grid grid-cols-1 gap-2 mb-5">
            {SCENES.map((s) => (
              <div key={s.id} className="flex items-center gap-2">
                <a
                  data-testid={`vo-audio-${s.index}`}
                  href={`${API}/media/${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-xs text-neutral-300 hover:text-orange-400 shrink-0 w-28"
                >
                  <Volume2 className="w-3.5 h-3.5" /> Scene {s.index}
                </a>
                <audio controls preload="none" src={`${API}/media/${s.id}`} className="h-8 w-full" />
              </div>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-[0.18em] text-orange-400 font-semibold mb-2">తెలుగు · Telugu</div>
          <div className="grid grid-cols-1 gap-2">
            {SCENES.map((s) => (
              <div key={`te-${s.id}`} className="flex items-center gap-2">
                <a
                  data-testid={`vo-audio-te-${s.index}`}
                  href={`${API}/media/te_${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-xs text-neutral-300 hover:text-orange-400 shrink-0 w-28"
                >
                  <Volume2 className="w-3.5 h-3.5" /> Scene {s.index}
                </a>
                <audio controls preload="none" src={`${API}/media/te_${s.id}`} className="h-8 w-full" />
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-neutral-600">{BRAND.poweredBy} · Partnerships: {BRAND.partnerEmail}</p>
        </div>
      </div>
    </div>
  );
}
